import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtPct, monthLabel } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Progress } from "@/components/Progress";
import DailyBrief from "@/components/DailyBrief";
import FocusStreak from "@/components/FocusStreak";
import { Check } from "@/components/Check";
import {
  ArrowUpRight,
  Briefcase,
  Camera,
  CalendarDays,
  CheckCircle2,
  Flame,
  Gauge,
  ListChecks,
  Lock,
  Target,
  TrendingUp,
} from "lucide-react";

const KPI = ({ label, value, sub, testid }) => (
  <div className="hud-frame p-5" data-testid={testid}>
    <div className="kpi-label">{label}</div>
    <div className="kpi-value mt-3 leading-none">{value}</div>
    {sub && (
      <div className="mono text-[10px] text-muted-foreground mt-2 tracking-[0.2em] uppercase">
        {sub}
      </div>
    )}
  </div>
);

const dayLabels = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

function HeatCell({ pct, date, count, total }) {
  const lvl =
    pct >= 0.85 ? 4 : pct >= 0.6 ? 3 : pct >= 0.35 ? 2 : pct > 0 ? 1 : 0;
  const colors = [
    "hsl(var(--surface-hover))",
    "hsl(var(--oxblood) / 0.25)",
    "hsl(var(--oxblood) / 0.5)",
    "hsl(var(--oxblood) / 0.75)",
    "hsl(var(--oxblood))",
  ];
  return (
    <div
      title={`${date} · ${count}/${total}`}
      className="w-full aspect-square border border-border/40"
      style={{ background: colors[lvl] }}
      data-testid={`heat-${date}`}
    />
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [settings, setSettings] = useState(null);

  const load = async () => {
    // Fire smart-deadline reshuffle quietly before we fetch the dashboard so
    // overdue tasks land on today before the brief is rendered (Pro only).
    if (user && user.plan !== "free") {
      try { await api.post("/ai/reshuffle"); } catch (_) { /* noop */ }
    }
    const [s, dash] = await Promise.all([
      api.get("/settings").then((r) => r.data),
      api.get("/dashboard").then((r) => r.data),
    ]);
    setSettings(s);
    setD(dash);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const todayHabit = useMemo(() => {
    if (!d) return null;
    const today = d.today;
    return d.heatmap.find((h) => h.date === today);
  }, [d]);

  const isPro = !!user && user.plan !== "free";

  const toggleToday = async (t, v) => {
    setD((prev) => ({
      ...prev,
      today_tasks: prev.today_tasks.map((x) => (x.id === t.id ? { ...x, done: v } : x)),
      today_done: prev.today_done + (v ? 1 : -1),
    }));
    await api.put(`/weekly-tasks/${t.id}`, { ...t, done: v });
  };

  if (!d) {
    return (
      <div className="mono text-muted-foreground uppercase tracking-widest text-xs">
        Loading…
      </div>
    );
  }

  return (
    <div className="space-y-10" data-testid="dashboard">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="kpi-label flex items-center gap-2">
            <span className="status-dot" />
            {(() => {
              const h = new Date().getHours();
              const greet = h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
              const name = (user?.name || user?.email?.split("@")[0] || "operator").trim();
              return `${greet}, ${name}`;
            })()}
            <span className="text-muted-foreground/60">·</span>
            <span>Overview · {monthLabel(d.month)}</span>
          </div>
          <h1 className="display text-3xl sm:text-5xl mt-3 leading-none font-bold uppercase tracking-tight">
            {(() => {
              const habit = d.habit_pct;
              const week = d.week_pct;
              if (habit >= 0.75 && week >= 0.75) return <>On<span className="text-hud">_</span>point.</>;
              if (habit < 0.35 || week < 0.35) return <>Off<span className="text-hud">_</span>track.</>;
              if (week >= 0.6) return <>On<span className="text-hud">_</span>track.</>;
              return <>Mid-week<span className="text-hud">_</span>reset.</>;
            })()}
          </h1>
          <p className="mono text-xs text-muted-foreground tracking-widest mt-3 uppercase">
            Habits {fmtPct(d.habit_pct)} · Week {fmtPct(d.week_pct)} · {d.active_deals} active deal{d.active_deals === 1 ? "" : "s"}
            {d.media_orders > 0 && ` · ${d.media_orders} media order${d.media_orders === 1 ? "" : "s"}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <FocusStreak />
          <Link to="/briefing" className="hud-btn hud-btn-primary flex items-center gap-2" data-testid="open-briefing">
            WEEKLY BRIEFING <ArrowUpRight size={12} />
          </Link>
        </div>
      </header>

      {/* AI Daily Brief */}
      <DailyBrief />

      {/* Command row: score · today's priorities · streaks · events */}
      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="hud-frame p-6" data-testid="card-score">
          <div className="kpi-label flex items-center gap-2"><Gauge size={11} /> PRODUCTIVITY SCORE</div>
          <div className="flex items-baseline gap-2 mt-3">
            <div className="display text-5xl font-bold leading-none">{d.productivity_score}</div>
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">/ 100</div>
          </div>
          <div className="mt-4"><Progress value={d.productivity_score / 100} label={false} /></div>
          <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground mt-3 space-y-1">
            <div>HABITS 7D {fmtPct(d.score_parts.habits7)} · WEEK {fmtPct(d.score_parts.week)}</div>
            <div>TODAY {fmtPct(d.score_parts.today)} · STREAK +{Math.min(d.score_parts.streak, 10)}</div>
          </div>
        </div>

        <div className="hud-frame p-6" data-testid="card-priorities">
          <div className="flex items-center justify-between">
            <div className="kpi-label flex items-center gap-2"><Target size={11} /> TODAY'S PRIORITIES</div>
            <span className="mono text-[10px] text-muted-foreground tabular-nums">{d.today_done}/{d.today_total}</span>
          </div>
          <div className="mt-3 space-y-1.5 max-h-44 overflow-y-auto">
            {d.today_tasks.length === 0 && (
              <div className="text-sm text-muted-foreground">
                Nothing scheduled today. <Link to="/weekly" className="text-oud underline">Plan your day →</Link>
              </div>
            )}
            {d.today_tasks.map((t) => (
              <div key={t.id} className="flex items-start gap-2" data-testid={`today-task-${t.id}`}>
                <div className="pt-0.5 flex-shrink-0">
                  <Check size="sm" checked={!!t.done} onChange={(v) => toggleToday(t, v)} testid={`today-check-${t.id}`} />
                </div>
                <span className={`text-sm flex-1 min-w-0 truncate ${t.done ? "line-through opacity-50" : ""}`}>{t.title}</span>
                {t.priority && (
                  <span className={`mono text-[8px] uppercase tracking-widest border px-1 flex-shrink-0 ${t.priority === "High" ? "text-[hsl(var(--oxblood))] border-[hsl(var(--oxblood))]" : t.priority === "Medium" ? "text-oud border-oud" : "text-muted-foreground border-border"}`}>{t.priority[0]}</span>
                )}
                {t.start_time && (
                  <span className="mono text-[9px] text-muted-foreground flex-shrink-0">{t.start_time}</span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="hud-frame p-6" data-testid="card-streaks">
          <div className="kpi-label flex items-center gap-2"><Flame size={11} /> CURRENT STREAKS</div>
          <div className="flex items-baseline gap-2 mt-3">
            <div className="display text-5xl font-bold leading-none">{d.overall_streak}</div>
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">DAY{d.overall_streak === 1 ? "" : "S"} ACTIVE</div>
          </div>
          <div className="mt-4 space-y-2">
            {d.habit_streaks.map((h) => (
              <div key={h.name} className="flex items-center justify-between gap-2">
                <span className="text-sm truncate">{h.name}</span>
                <span className="mono text-[10px] text-oud tabular-nums whitespace-nowrap">{h.streak}D</span>
              </div>
            ))}
          </div>
        </div>

        {isPro ? (
          <div className="hud-frame p-6" data-testid="card-events">
            <div className="flex items-center justify-between">
              <div className="kpi-label flex items-center gap-2"><CalendarDays size={11} /> NEXT 7 DAYS</div>
              <Link to="/calendar" className="hud-btn !px-2 !py-1"><ArrowUpRight size={11} /></Link>
            </div>
            <div className="mt-3 space-y-2 max-h-44 overflow-y-auto">
              {d.upcoming_events.length === 0 && (
                <div className="text-sm text-muted-foreground">No events scheduled.</div>
              )}
              {d.upcoming_events.map((e) => (
                <div key={e.id} className="flex items-center gap-2">
                  <span className="mono text-[9px] uppercase tracking-widest text-oud whitespace-nowrap">
                    {new Date(e.date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    {e.time ? ` ${e.time}` : ""}
                  </span>
                  <span className="text-sm truncate flex-1">{e.title || e.type || "Event"}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="hud-frame p-6 flex flex-col items-start justify-center gap-3" data-testid="card-events-locked">
            <Lock size={14} className="text-oud" />
            <div className="kpi-label">CALENDAR · NEXT 7 DAYS</div>
            <p className="text-sm text-muted-foreground">See upcoming events and sync with Google Calendar on Pro.</p>
            <Link to="/billing" className="hud-btn hud-btn-primary !text-[10px]">UPGRADE</Link>
          </div>
        )}
      </section>

      {/* Analytics: weekly + monthly trends */}
      {isPro ? (
        <section className="grid grid-cols-1 xl:grid-cols-2 gap-4" data-testid="analytics-section">
          <div className="hud-frame p-6">
            <div className="kpi-label flex items-center gap-2"><TrendingUp size={11} /> WEEKLY ANALYTICS · TASK COMPLETION</div>
            <div className="flex items-end gap-2 h-32 mt-5">
              {d.weekly_trend.map((w) => (
                <div key={w.week_start} className="flex-1 flex flex-col items-center gap-1.5" title={`${w.week_start} · ${w.done}/${w.total}`}>
                  <div className="w-full bg-[hsl(var(--surface))] border border-border/40 relative flex items-end" style={{ height: "100%" }}>
                    <div className="w-full bg-[hsl(var(--oxblood))]" style={{ height: `${Math.round(w.pct * 100)}%` }} />
                  </div>
                  <span className="mono text-[8px] text-muted-foreground">{w.week_start.slice(5)}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="hud-frame p-6">
            <div className="kpi-label flex items-center gap-2"><TrendingUp size={11} /> MONTHLY ANALYTICS · HABIT CONSISTENCY</div>
            <div className="flex items-end gap-2 h-32 mt-5">
              {d.monthly_trend.map((mo) => (
                <div key={mo.month} className="flex-1 flex flex-col items-center gap-1.5" title={`${mo.month} · ${Math.round(mo.pct * 100)}%`}>
                  <div className="w-full bg-[hsl(var(--surface))] border border-border/40 relative flex items-end" style={{ height: "100%" }}>
                    <div className="w-full bg-oud" style={{ height: `${Math.round(mo.pct * 100)}%`, background: "hsl(var(--oud, 36 45% 55%))" }} />
                  </div>
                  <span className="mono text-[8px] text-muted-foreground">{mo.month.slice(2)}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : (
        <section className="hud-frame p-6 flex items-center justify-between gap-4" data-testid="analytics-locked">
          <div className="flex items-center gap-3">
            <Lock size={14} className="text-oud" />
            <div>
              <div className="kpi-label">WEEKLY &amp; MONTHLY ANALYTICS</div>
              <div className="text-sm text-muted-foreground mt-1">8-week task trends and 6-month habit consistency — Pro feature.</div>
            </div>
          </div>
          <Link to="/billing" className="hud-btn hud-btn-primary whitespace-nowrap">UPGRADE TO PRO</Link>
        </section>
      )}


      {/* KPI Row */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI
          label="Monthly Habits"
          value={fmtPct(d.habit_pct)}
          sub={`${d.habit_rows.length} HABITS · ${d.heatmap.length} DAYS`}
          testid="kpi-habits"
        />
        <KPI
          label="Weekly Tasks"
          value={fmtPct(d.week_pct)}
          sub="CURRENT WEEK"
          testid="kpi-week"
        />
        <KPI
          label="Projected Commission"
          value={fmtMoney(d.projected_commission)}
          sub={`${d.closed_deals} CLOSED · ${d.active_deals} ACTIVE`}
          testid="kpi-commission"
        />
        <KPI
          label="Media Revenue"
          value={fmtMoney(d.media_revenue)}
          sub={`${d.media_orders} ORDERS · ${d.media_paid} PAID`}
          testid="kpi-media-rev"
        />
      </section>

      {/* Heatmap + week */}
      <section className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="hud-frame p-6 xl:col-span-2" data-testid="heatmap-card">
          <div className="flex items-end justify-between mb-5">
            <div>
              <div className="kpi-label flex items-center gap-2">
                <Flame size={11} /> MONTHLY HABIT HEATMAP
              </div>
              <div className="display text-2xl mt-1">
                {monthLabel(d.month)}
              </div>
            </div>
            <Link
              to="/month"
              className="hud-btn flex items-center gap-2"
              data-testid="goto-month"
            >
              OPEN MONTH <ArrowUpRight size={12} />
            </Link>
          </div>
          <div
            className="grid gap-1.5"
            style={{
              gridTemplateColumns: "repeat(auto-fill, minmax(22px, 1fr))",
            }}
          >
            {d.heatmap.map((h) => (
              <HeatCell
                key={h.date}
                pct={h.pct}
                date={h.date}
                count={h.count}
                total={h.total}
              />
            ))}
          </div>
          <div className="flex items-center gap-3 mt-5 mono text-[10px] text-muted-foreground uppercase tracking-widest">
            <span>LESS</span>
            {[0, 1, 2, 3, 4].map((lvl) => (
              <span
                key={lvl}
                className="w-3 h-3 border border-border/40"
                style={{
                  background: [
                    "hsl(var(--surface-hover))",
                    "hsl(var(--oxblood) / 0.25)",
                    "hsl(var(--oxblood) / 0.5)",
                    "hsl(var(--oxblood) / 0.75)",
                    "hsl(var(--oxblood))",
                  ][lvl],
                }}
              />
            ))}
            <span>MORE</span>
          </div>
        </div>

        <div className="hud-frame p-6 space-y-5" data-testid="week-card">
          <div className="flex items-end justify-between">
            <div>
              <div className="kpi-label flex items-center gap-2">
                <ListChecks size={11} /> THIS WEEK
              </div>
              <div className="display text-2xl mt-1">{fmtPct(d.week_pct)}</div>
            </div>
            <Link to="/weekly" className="hud-btn" data-testid="goto-week">
              OPEN
            </Link>
          </div>
          <div className="space-y-2.5">
            {d.per_day_week.map((p, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="mono text-[10px] uppercase tracking-widest w-9 text-muted-foreground">
                  {dayLabels[i]}
                </div>
                <Progress value={p.pct} />
                <div className="mono text-[10px] text-muted-foreground w-10 text-right tabular-nums">
                  {p.done}/{p.total}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Habit list + secondary stats */}
      <section className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="hud-frame p-6 xl:col-span-2" data-testid="habit-detail">
          <div className="kpi-label">HABIT PROGRESS · {monthLabel(d.month)}</div>
          <div className="display text-2xl mt-1 mb-5">By the numbers</div>
          <div className="space-y-3">
            {d.habit_rows.map((h) => (
              <div
                key={h.id}
                className="grid grid-cols-12 items-center gap-3 py-1"
                data-testid={`habit-summary-${h.id}`}
              >
                <div className="col-span-5 sm:col-span-4 text-sm truncate">
                  {h.name}
                </div>
                <div className="col-span-5 sm:col-span-6">
                  <Progress value={h.pct} label={false} />
                </div>
                <div className="col-span-2 mono text-xs text-right tabular-nums">
                  {h.completed}/{h.days}
                </div>
              </div>
            ))}
            {d.habit_rows.length === 0 && (
              <div className="text-muted-foreground text-sm">
                No habits yet. Add some in Month View or Settings.
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="hud-frame p-6" data-testid="card-deals">
            <div className="flex items-start justify-between">
              <div>
                <div className="kpi-label flex items-center gap-2">
                  <Briefcase size={11} /> DEAL PIPELINE
                </div>
                <div className="display text-3xl mt-1">
                  {fmtMoney(d.projected_commission)}
                </div>
              </div>
              <Link to="/deals" className="hud-btn">
                <ArrowUpRight size={12} />
              </Link>
            </div>
            <div className="hud-divider my-4" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="kpi-label">CLOSED</div>
                <div className="display text-xl">{d.closed_deals}</div>
              </div>
              <div>
                <div className="kpi-label">ACTIVE</div>
                <div className="display text-xl">{d.active_deals}</div>
              </div>
            </div>
          </div>

          <div className="hud-frame p-6" data-testid="card-media">
            <div className="flex items-start justify-between">
              <div>
                <div className="kpi-label flex items-center gap-2">
                  <Camera size={11} /> RE MEDIA
                </div>
                <div className="display text-3xl mt-1">
                  {fmtMoney(d.media_revenue)}
                </div>
              </div>
              <Link to="/media" className="hud-btn">
                <ArrowUpRight size={12} />
              </Link>
            </div>
            <div className="hud-divider my-4" />
            <div className="grid grid-cols-3 gap-2">
              <div>
                <div className="kpi-label">ORDERS</div>
                <div className="display text-lg">{d.media_orders}</div>
              </div>
              <div>
                <div className="kpi-label">PAID</div>
                <div className="display text-lg">{d.media_paid}</div>
              </div>
              <div>
                <div className="kpi-label">DONE</div>
                <div className="display text-lg flex items-center gap-1">
                  {d.media_completed}
                  <CheckCircle2 size={14} className="text-[hsl(var(--oxblood))]" />
                </div>
              </div>
            </div>
          </div>

          {todayHabit && (
            <div className="hud-frame p-6" data-testid="card-today">
              <div className="kpi-label">TODAY</div>
              <div className="display text-3xl mt-1">
                {fmtPct(todayHabit.pct)}
              </div>
              <div className="mono text-[11px] text-muted-foreground mt-1">
                {todayHabit.count} of {todayHabit.total} habits checked
              </div>
              <div className="mt-3">
                <Progress value={todayHabit.pct} label={false} />
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
